import {Button, DropdownMenu, Icon} from '@wandb/components';
import {useRef, useState} from 'react';
import {Button as AriaButton} from 'react-aria-components';

import {catalogHref, shellRouteClaimTable} from '../routeClaims';
import {forgeProducts, type ForgeProduct} from './forgeProducts';
import {
  INFRA_CONTACT_SALES_URL,
  infraConsoleHref,
  infraProducts,
} from './infraProducts';
import {useInfraSession} from './useInfraSession';
import css from './AppLauncher.module.css';

type Props = {
  currentProduct?: ForgeProduct;
};

export const AppLauncher = ({currentProduct}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const infraSession = useInfraSession(isOpen);

  return (
    <div className={css.launcher}>
      {/* Outlined trigger from the Axiom navigation spec; the component
          library has no outlined IconButton yet, so swap this for the Axiom
          component once it ships. */}
      <AriaButton
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Open app launcher"
        className={css.trigger}
        onPress={() => setIsOpen(open => !open)}>
        <Icon icon="sweep-grid" size="md" />
      </AriaButton>
      <DropdownMenu.Content
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
        placement="bottom start"
        aria-label="Forge applications">
        <DropdownMenu.Section>
          <DropdownMenu.Header>
            <span className={css.brand}>CoreWeave</span>
          </DropdownMenu.Header>
        </DropdownMenu.Section>
        <DropdownMenu.Separator />
        <DropdownMenu.Section>
          <DropdownMenu.Header>
            <span className={css.sectionHeader}>
              <span className={css.sectionTitle}>FORGE</span>
              {currentProduct != null && (
                <span className={css.current}>Current</span>
              )}
            </span>
          </DropdownMenu.Header>
          {forgeProducts.map(product => {
            const isCurrent = currentProduct?.id === product.id;
            return (
              <DropdownMenu.Item
                key={product.id}
                data-current={isCurrent || undefined}
                href={
                  catalogHref(product, shellRouteClaimTable()) ??
                  product.path ??
                  `/${product.id}`
                }>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={
                    isCurrent
                      ? {icon: product.icon, color: 'blue'}
                      : {icon: product.icon}
                  }
                />
                <DropdownMenu.ItemLabel>{product.label}</DropdownMenu.ItemLabel>
                <DropdownMenu.ItemDescription>
                  {product.description}
                </DropdownMenu.ItemDescription>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Section>
        <DropdownMenu.Separator />
        <DropdownMenu.Section>
          <DropdownMenu.Header>
            <span className={css.sectionHeader}>
              <span className={css.sectionTitle}>INFRASTRUCTURE</span>
            </span>
          </DropdownMenu.Header>
          {infraSession === 'pending' && (
            <DropdownMenu.Header>
              <div
                aria-busy="true"
                aria-label="Checking infrastructure session"
                className={css.infraSkeleton}
                role="status">
                <div className={css.skeletonText}>
                  <span className={css.skeletonLine} />
                  <span className={css.skeletonLine} />
                </div>
                <div className={css.infraActions}>
                  <span className={css.skeletonButton} />
                  <span className={css.skeletonButton} />
                </div>
              </div>
            </DropdownMenu.Header>
          )}
          {infraSession === 'signed-in' &&
            infraProducts.map(product => (
              <DropdownMenu.Item
                key={product.id}
                href={infraConsoleHref(product.path)}>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={{icon: product.icon}}
                />
                <DropdownMenu.ItemLabel>{product.label}</DropdownMenu.ItemLabel>
                <DropdownMenu.ItemDescription>
                  {product.description}
                </DropdownMenu.ItemDescription>
              </DropdownMenu.Item>
            ))}
          {infraSession === 'signed-out' && (
            <DropdownMenu.Header>
              <div className={css.infraPrompt}>
                <p className={css.infraBlurb}>
                  Provision and manage the compute, networking, and storage that
                  power AI workloads.
                </p>
                <div className={css.infraActions}>
                  <Button
                    href={INFRA_CONTACT_SALES_URL}
                    size="sm"
                    variant="secondary">
                    Contact sales
                  </Button>
                  <Button
                    href={infraConsoleHref('/')}
                    size="sm"
                    startIcon="log-in"
                    variant="ghost">
                    Sign in
                  </Button>
                </div>
              </div>
            </DropdownMenu.Header>
          )}
        </DropdownMenu.Section>
      </DropdownMenu.Content>
    </div>
  );
};
